import { useEvalEvents } from 'features/evaluations/metadata/hooks/useEvalEvents'
import { parseDocumentPageMetadataUserEditEntries } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { cantileverDocumentPageIdForPage } from 'features/knowledgeDocumentViewer/utils/cantileverHostIds'
import { documentPageMetadataUserEditsByScope } from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { deferReasonIfLatestUserEditDeferred } from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalHistory'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useCallback, useMemo } from 'react'

export function useEvalPageEdits(
  siteId: string,
  documentId: string | undefined,
  pageNum: number | undefined,
  userEmail: string | undefined
) {
  const tags = useMemo(
    () =>
      documentId && pageNum
        ? {
            site: siteId,
            'cantilever-document-page': cantileverDocumentPageIdForPage(
              siteId,
              documentId,
              pageNum
            ),
          }
        : undefined,
    [siteId, documentId, pageNum]
  )
  const own = useEvalEvents(
    tags && userEmail
      ? documentPageMetadataUserEditsByScope(tags, {
          createdBy: userEmail,
          surface: 'eval',
          limit: 100,
        })
      : undefined
  )
  // A newest-first limit-one lookup must not use the paginated metrics loader.
  const latest = useGetRolodexEntriesByLoadOptions<unknown>(
    tags ? documentPageMetadataUserEditsByScope(tags, { limit: 1 }) : undefined,
    { keepPreviousData: false }
  )
  const deferReason = useMemo(
    () =>
      latest.data === undefined
        ? undefined
        : deferReasonIfLatestUserEditDeferred(
            parseDocumentPageMetadataUserEditEntries(latest.data)
          ),
    [latest.data]
  )
  const retryOwn = own.mutate
  const retryLatest = latest.mutate
  const retry = useCallback(() => {
    void retryOwn().catch(() => undefined)
    void retryLatest().catch(() => undefined)
  }, [retryOwn, retryLatest])
  return {
    edits: own.data,
    isLoading: own.isLoading || latest.isLoading,
    error: own.error ?? latest.error,
    deferReason,
    retry,
  }
}
